import JSZip from 'jszip';
import { CharacterDefinition } from '@hexcrawl/shared-utils';
import { ExtractedFrame } from './types.ts';
import { loadManifest } from './manifest.ts';
import { assembleAnimation } from './layerCompositor.ts';
import { extractFramesFromSheet } from './frameExtractor.ts';
import { pack } from './atlasPacker.ts';
import { renderAtlasPages } from './atlasRenderer.ts';
import { generateMultiatlasJSON, generateAnimsJSON, MultiatlasJSON, AnimsJSON } from './jsonGenerators.ts';

const MAX_TEXTURE_SIZE = 2048;
const ATLAS_PADDING = 1;
const ATLAS_PREFIX = 'character';

export interface AssembledSprite {
  pages: ImageBitmap[];
  atlasJSON: MultiatlasJSON;
  animsJSON: AnimsJSON;
}

/**
 * Assemble the full sprite atlas for a character definition.
 * Equivalent to the server's buildSpriteZip(), but produces ImageBitmaps
 * and JSON objects directly rather than a ZIP buffer.
 */
export async function assembleSprite(definition: CharacterDefinition): Promise<AssembledSprite> {
  const manifest = await loadManifest();

  // Build outputKey → animFolder map (same logic as server)
  const outputKeyToFolder = new Map<string, string>();
  for (const [animFolder, animData] of Object.entries(manifest.animations)) {
    if (animData.defaultOutputKey) {
      outputKeyToFolder.set(animData.defaultOutputKey, animFolder);
    }
  }
  if (definition.animationOverrides) {
    for (const [outputKey, animFolder] of Object.entries(definition.animationOverrides)) {
      outputKeyToFolder.set(outputKey, animFolder);
    }
  }

  // Assemble each animation and extract frames
  const allFrames: ExtractedFrame[] = [];
  for (const [outputKey, animFolder] of outputKeyToFolder) {
    const animData = manifest.animations[animFolder];
    if (!animData) {
      console.warn(`[SpriteAssembler] Animation "${animFolder}" not in manifest — skipping "${outputKey}".`);
      continue;
    }
    console.log(`[SpriteAssembler] Assembling ${animFolder} (${outputKey})...`);
    const sheet = await assembleAnimation(animFolder, definition, manifest);
    const frames = await extractFramesFromSheet(
      outputKey, sheet, animData.columnCount, animData.frameWidth, animData.frameHeight,
    );
    sheet.close();
    allFrames.push(...frames);
  }

  if (allFrames.length === 0) {
    throw new Error('No frames extracted — check animation definitions and layer configuration.');
  }

  const packedTextures = pack(allFrames, MAX_TEXTURE_SIZE, ATLAS_PADDING);
  const pages = await renderAtlasPages(packedTextures);
  const atlasJSON = generateMultiatlasJSON(packedTextures, ATLAS_PREFIX);
  const animsJSON = generateAnimsJSON(outputKeyToFolder, allFrames, manifest, ATLAS_PREFIX);

  // Free per-frame bitmaps — they've been composited into the atlas pages
  for (const frame of allFrames) frame.bitmap.close();

  return { pages, atlasJSON, animsJSON };
}

/**
 * Assemble only the idle-south frame for use as a character portrait preview.
 * Much faster than a full assembleSprite() since only one animation is processed.
 */
export async function assemblePreviewFrame(definition: CharacterDefinition): Promise<ImageBitmap> {
  const manifest = await loadManifest();

  // Resolve the idle animation folder
  let idleFolder = 'Idle';
  for (const [folder, data] of Object.entries(manifest.animations)) {
    if (data.defaultOutputKey === 'idle') { idleFolder = folder; break; }
  }
  if (definition.animationOverrides?.['idle']) {
    idleFolder = definition.animationOverrides['idle'];
  }

  const animData = manifest.animations[idleFolder];
  if (!animData) throw new Error(`Idle animation folder "${idleFolder}" not found in manifest.`);

  const sheet = await assembleAnimation(idleFolder, definition, manifest);

  // Extract the south-facing first frame (column 0, row 0)
  const { frameWidth, frameHeight } = animData;
  const canvas = new OffscreenCanvas(frameWidth, frameHeight);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(sheet, 0, 0, frameWidth, frameHeight, 0, 0, frameWidth, frameHeight);
  sheet.close();

  return canvas.transferToImageBitmap();
}

/**
 * Extract a named frame from an already-assembled sprite for display.
 * Falls back to the first available frame if the named one is not found.
 */
export function extractFrameFromAssembled(
  assembled: AssembledSprite,
  frameName = 'idle_south_00',
): ImageBitmap | null {
  for (let i = 0; i < assembled.atlasJSON.textures.length; i++) {
    const texture = assembled.atlasJSON.textures[i];
    const entry = texture.frames.find(f => f.filename === frameName);
    if (entry && assembled.pages[i]) {
      const { x, y, w, h } = entry.frame;
      const canvas = new OffscreenCanvas(w, h);
      canvas.getContext('2d')!.drawImage(assembled.pages[i], x, y, w, h, 0, 0, w, h);
      return canvas.transferToImageBitmap();
    }
  }
  // Fallback: first frame on first page
  const firstTexture = assembled.atlasJSON.textures[0];
  if (firstTexture?.frames.length > 0 && assembled.pages[0]) {
    const { x, y, w, h } = firstTexture.frames[0].frame;
    const canvas = new OffscreenCanvas(w, h);
    canvas.getContext('2d')!.drawImage(assembled.pages[0], x, y, w, h, 0, 0, w, h);
    return canvas.transferToImageBitmap();
  }
  return null;
}

interface AtlasFrame {
  filename: string;
  frame: { x: number; y: number; w: number; h: number };
  trimmed?: boolean;
  spriteSourceSize?: { x: number; y: number; w: number; h: number };
  sourceSize?: { w: number; h: number };
}

interface AtlasTexture {
  image: string;
  frames: AtlasFrame[];
}

/**
 * Load an idle_south preview frame from a static sprite zip file.
 * Fetches the zip, parses the multiatlas JSON, and renders the frame at its
 * correct position within the full source canvas (accounting for trim offsets).
 *
 * Falls back to any idle frame, then to the first frame in the atlas.
 */
export async function loadStaticSpritePreviewFrame(zipFilename: string): Promise<ImageBitmap> {
  const url = `/assets/sprites/${zipFilename}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch sprite zip: ${url}`);

  const zip = await JSZip.loadAsync(await response.arrayBuffer());

  // Find the multiatlas JSON (the file not ending in _anims.json)
  const atlasFile = Object.values(zip.files).find(
    f => f.name.endsWith('.json') && !f.name.endsWith('_anims.json')
  );
  if (!atlasFile) throw new Error(`No atlas JSON found in ${zipFilename}`);

  const atlas = JSON.parse(await atlasFile.async('text')) as { textures: AtlasTexture[] };

  // Search all textures for the best frame: prefer idle_south_00, then any idle frame, then first frame overall
  let bestEntry: AtlasFrame | null = null;
  let bestTexture: AtlasTexture | null = null;

  for (const texture of atlas.textures) {
    const exact = texture.frames.find(f => f.filename === 'idle_south_00');
    if (exact) { bestEntry = exact; bestTexture = texture; break; }

    if (!bestEntry) {
      const idle = texture.frames.find(f => f.filename.startsWith('idle'));
      if (idle) { bestEntry = idle; bestTexture = texture; }
      else if (texture.frames.length > 0) { bestEntry = texture.frames[0]; bestTexture = texture; }
    }
  }

  if (!bestEntry || !bestTexture) throw new Error(`No usable frames found in ${zipFilename}`);

  const pngFile = zip.file(bestTexture.image);
  if (!pngFile) throw new Error(`PNG page "${bestTexture.image}" not found in ${zipFilename}`);

  const pngBlob = new Blob([await pngFile.async('arraybuffer')], { type: 'image/png' });
  const page = await createImageBitmap(pngBlob);

  // Use the full source dimensions so trimmed sprites are positioned correctly
  const canvasW = bestEntry.sourceSize?.w ?? bestEntry.frame.w;
  const canvasH = bestEntry.sourceSize?.h ?? bestEntry.frame.h;
  const destX = bestEntry.spriteSourceSize?.x ?? 0;
  const destY = bestEntry.spriteSourceSize?.y ?? 0;
  const { x, y, w, h } = bestEntry.frame;

  const canvas = new OffscreenCanvas(canvasW, canvasH);
  canvas.getContext('2d')!.drawImage(page, x, y, w, h, destX, destY, w, h);
  page.close();

  return canvas.transferToImageBitmap();
}
